// Phase 75 — Football Training Plans tab inside Performance Programs.
// Backend enforces RBAC; this component just renders what the API returns.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Plus, Pencil, Trash2, ShieldCheck, MapPin, Clock } from 'lucide-react';
import TargetAudienceSelector from './TargetAudienceSelector';
import ResourcesField, { ResourcesView } from './ResourcesField';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  'Technical', 'Tactical', 'Game Model / Team Principles',
  'Position-Specific / Unit Training', 'Attacking', 'Defending',
  'Transitions', 'Set Pieces', 'Match Preparation', 'Goalkeeper',
  'Warm-Up / Activation', 'Recovery', 'Futsal', 'Other',
];
const PHASE_OF_PLAY = [
  { v: 'attacking', l: 'Attacking' },
  { v: 'defending', l: 'Defending' },
  { v: 'transition_to_attack', l: 'Transition → Attack' },
  { v: 'transition_to_defend', l: 'Transition → Defend' },
  { v: 'set_piece', l: 'Set Piece' },
  { v: 'mixed', l: 'Mixed' },
];
const FOCUS_AREAS = [
  { v: 'technical', l: 'Technical' },
  { v: 'tactical', l: 'Tactical' },
  { v: 'physical_with_ball', l: 'Physical (with ball)' },
  { v: 'psychological_decision_making', l: 'Psychological / Decision-Making' },
  { v: 'team_organization', l: 'Team Organization' },
];
const PITCH_AREAS = [
  { v: 'full_pitch', l: 'Full pitch' },
  { v: 'half_pitch', l: 'Half pitch' },
  { v: 'final_third', l: 'Final third' },
  { v: 'middle_third', l: 'Middle third' },
  { v: 'defensive_third', l: 'Defensive third' },
  { v: 'wide_areas', l: 'Wide areas' },
  { v: 'central_areas', l: 'Central areas' },
  { v: 'box', l: 'Box / Penalty area' },
];
const STATUSES = ['draft', 'active', 'completed', 'archived'];
const INTENSITIES = ['low', 'medium', 'high'];

const EMPTY = {
  title: '', category: 'Tactical', objective: '', description: '',
  target_type: 'custom', target_squads: [], target_athletes: [], target_tag_ids: [],
  intensity: 'medium', duration_minutes: 60, status: 'draft',
  phase_of_play: 'mixed', focus_area: 'tactical', pitch_area: 'full_pitch',
  players_required: '', equipment_needed: '',
  warm_up: '', activation: '', main_drill: '', progression: '',
  game_scenario: '', coaching_points: '', cool_down: '',
  success_criteria: '', notes: '',
  attachments: [],
};

export default function FootballTrainingPlansTab({ athletes = [], tags = [], canEdit, canDelete, athleteId = null }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);

  // Phase 6 — refetch when athleteId changes; if no athlete is set, show empty.
  useEffect(() => { fetchPlans(); }, [athleteId]);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const url = athleteId
        ? `${API}/football-training-plans?athlete_id=${athleteId}`
        : `${API}/football-training-plans`;
      const r = await axios.get(url);
      setPlans(r.data || []);
    } catch (err) {
      if (err.response?.status !== 403) toast.error('Failed to load Football Training Plans');
    } finally { setLoading(false); }
  };

  const openCreate = () => { setForm(EMPTY); setEditingId(null); setCreateOpen(true); };
  const openEdit = (p) => {
    setForm({
      ...EMPTY,
      ...p,
      target_squads: p.target_squads || [],
      target_athletes: p.target_athletes || [],
      target_tag_ids: p.target_tag_ids || [],
      attachments: p.attachments || [],
    });
    setEditingId(p.id);
    setCreateOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    const payload = {
      ...form,
      players_required: form.players_required === '' ? null : Number(form.players_required),
      duration_minutes: form.duration_minutes === '' ? null : Number(form.duration_minutes),
    };
    try {
      if (editingId) {
        await axios.put(`${API}/football-training-plans/${editingId}`, payload);
        toast.success('Football Training Plan updated');
      } else {
        await axios.post(`${API}/football-training-plans`, payload);
        toast.success('Football Training Plan created');
      }
      setCreateOpen(false); setForm(EMPTY); setEditingId(null);
      fetchPlans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this Football Training Plan? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/football-training-plans/${id}`);
      toast.success('Deleted');
      fetchPlans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const filtered = filter === 'all' ? plans : plans.filter(p => p.category === filter);
  // Phase 6 — hide empty categories from the filter chips. The "All" chip
  // is always shown so users can clear the filter quickly.
  const nonEmptyCategories = CATEGORIES.filter(c => plans.some(p => p.category === c));
  const filterChips = ['all', ...nonEmptyCategories];
  const getTargetLabel = (p) => {
    if (p.target_type === 'all') return 'All Athletes';
    const parts = [...(p.target_squads || [])];
    if ((p.target_athletes || []).length) parts.push(`${p.target_athletes.length} player(s)`);
    if ((p.target_tag_ids || []).length) parts.push(`${p.target_tag_ids.length} tag(s)`);
    return parts.join(' · ') || 'No target';
  };

  return (
    <div data-testid="football-plans-tab">
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <div className="flex gap-1.5 flex-wrap">
          {filterChips.map(c => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                filter === c
                  ? 'bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-900'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200'
              }`}
              data-testid={`football-filter-${c.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {c === 'all' ? 'All' : c}
            </button>
          ))}
        </div>
        {canEdit && (
          <Button
            onClick={openCreate}
            className="bg-pfap-green hover:bg-pfap-green/90 h-9"
            data-testid="create-football-plan-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> New Football Plan
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600" />
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ShieldCheck className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-gray-500 text-sm" data-testid="football-plans-empty">
              No Football Training Plans yet.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="football-plans-grid">
          {filtered.map(p => (
            <Card
              key={p.id}
              className="border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
              data-testid={`football-card-${p.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <Badge className="text-[10px] px-1.5 py-0 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border border-emerald-200">
                    {p.category}
                  </Badge>
                  {canEdit && (
                    <div className="flex items-center gap-0.5">
                      <Button
                        size="sm" variant="ghost"
                        className="h-6 w-6 p-0 text-gray-500 hover:text-gray-900"
                        onClick={() => openEdit(p)}
                        data-testid={`edit-football-${p.id}`}
                      ><Pencil className="w-3 h-3" /></Button>
                      {canDelete && (
                        <Button
                          size="sm" variant="ghost"
                          className="h-6 w-6 p-0 text-red-500"
                          onClick={() => handleDelete(p.id)}
                          data-testid={`delete-football-${p.id}`}
                        ><Trash2 className="w-3 h-3" /></Button>
                      )}
                    </div>
                  )}
                </div>
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">{p.title}</p>
                {p.objective && (
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-2 line-clamp-2">{p.objective}</p>
                )}
                <div className="flex flex-wrap gap-1 mb-2">
                  {p.intensity && <Badge variant="outline" className="text-[10px] px-1.5 py-0 capitalize">{p.intensity}</Badge>}
                  {p.phase_of_play && <Badge variant="outline" className="text-[10px] px-1.5 py-0 capitalize">{p.phase_of_play.replace(/_/g, ' ')}</Badge>}
                  {p.status && <Badge variant="outline" className="text-[10px] px-1.5 py-0 capitalize">{p.status}</Badge>}
                </div>
                <p className="text-[10px] text-gray-400 flex items-center gap-2 flex-wrap">
                  <span className="flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" />{p.duration_minutes || '—'}m</span>
                  <span className="flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{(p.pitch_area || 'full_pitch').replace(/_/g, ' ')}</span>
                  <span>· {getTargetLabel(p)}</span>
                  {(p.attachments || []).length > 0 && (
                    <span data-testid={`football-resource-count-${p.id}`}>· 📎 {p.attachments.length}</span>
                  )}
                </p>
                {(p.attachments || []).length > 0 && (
                  <div className="mt-2">
                    <ResourcesView attachments={p.attachments} data-testid={`football-resources-${p.id}`} />
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) { setForm(EMPTY); setEditingId(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="football-plan-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Football Training Plan' : 'New Football Training Plan'}</DialogTitle>
            <DialogDescription className="text-xs">
              Football/pitch session — assign to all athletes, squads, tags, or specific players.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
            <div className="sm:col-span-2">
              <Label className="text-xs">Title *</Label>
              <Input
                value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. 4v3 + 1 Counter-press in middle third"
                data-testid="football-title-input"
              />
            </div>

            <div>
              <Label className="text-xs">Category</Label>
              <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="football-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Intensity</Label>
              <Select value={form.intensity} onValueChange={v => setForm({ ...form, intensity: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{INTENSITIES.map(i => <SelectItem key={i} value={i} className="capitalize">{i}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">Phase of play</Label>
              <Select value={form.phase_of_play} onValueChange={v => setForm({ ...form, phase_of_play: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PHASE_OF_PLAY.map(p => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Focus area</Label>
              <Select value={form.focus_area} onValueChange={v => setForm({ ...form, focus_area: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{FOCUS_AREAS.map(f => <SelectItem key={f.v} value={f.v}>{f.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">Pitch area</Label>
              <Select value={form.pitch_area} onValueChange={v => setForm({ ...form, pitch_area: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PITCH_AREAS.map(p => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">Duration (mins)</Label>
              <Input type="number" min="0" value={form.duration_minutes}
                onChange={e => setForm({ ...form, duration_minutes: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs">Players required</Label>
              <Input type="number" min="0" value={form.players_required}
                onChange={e => setForm({ ...form, players_required: e.target.value })}
              />
            </div>

            <div className="sm:col-span-2">
              <Label className="text-xs">Objective</Label>
              <Input value={form.objective} onChange={e => setForm({ ...form, objective: e.target.value })} placeholder="What this session is about" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Description</Label>
              <Textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            </div>

            <div className="sm:col-span-2">
              <Label className="text-xs">Target audience</Label>
              <TargetAudienceSelector
                value={{
                  target_type: form.target_type,
                  target_squads: form.target_squads,
                  target_athletes: form.target_athletes,
                  target_tag_ids: form.target_tag_ids,
                }}
                onChange={(v) => setForm({ ...form, ...v })}
                athletes={athletes}
                tags={tags}
              />
            </div>

            <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Warm-up</Label>
                <Textarea rows={2} value={form.warm_up} onChange={e => setForm({ ...form, warm_up: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Main drill</Label>
                <Textarea rows={2} value={form.main_drill} onChange={e => setForm({ ...form, main_drill: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Progression</Label>
                <Textarea rows={2} value={form.progression} onChange={e => setForm({ ...form, progression: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Game scenario</Label>
                <Textarea rows={2} value={form.game_scenario} onChange={e => setForm({ ...form, game_scenario: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Coaching points</Label>
                <Textarea rows={2} value={form.coaching_points} onChange={e => setForm({ ...form, coaching_points: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Success criteria</Label>
                <Textarea rows={2} value={form.success_criteria} onChange={e => setForm({ ...form, success_criteria: e.target.value })} />
              </div>
            </div>

            <div className="sm:col-span-2">
              <Label className="text-xs">Equipment needed</Label>
              <Input value={form.equipment_needed} onChange={e => setForm({ ...form, equipment_needed: e.target.value })} placeholder="e.g. 12 cones, 2 mini goals, 6 bibs" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </div>

            <div className="sm:col-span-2">
              <ResourcesField
                value={form.attachments || []}
                onChange={val => setForm({ ...form, attachments: val })}
                data-testid="football-plan-resources"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSave}
              className="bg-pfap-green hover:bg-pfap-green/90"
              data-testid="football-plan-save-btn"
            >{editingId ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
